<template>
    <el-dialog
        v-model="visible"
        :width="width"
        :show-close="showClose"
        :align-center="alignCenter"
        :close-on-click-modal="closeOnClickModal"
        :close-on-press-escape="closeOnPressEscape"
        :before-close="handleBeforeClose"
        :append-to-body="appendToBody"
        class="kpl-dialog"
    >
        <template #header>
            <slot name="title">
                <div class="kpl-dialog__title">{{ title }}</div>
            </slot>
        </template>

        <div class="kpl-dialog__body">
            <slot>
                <div v-if="content" class="kpl-dialog__content">{{ content }}</div>
            </slot>
        </div>

        <template v-if="showFooter" #footer>
            <slot name="footer">
                <div class="kpl-dialog__footer">
                    <template v-if="buttons.length">
                        <el-button
                            v-for="(btn, index) in buttons"
                            :key="index"
                            class="kpl-dialog__btn"
                            :class="btn.className"
                            :color="btn.color || (btn.type === 'primary' ? '#386AEA' : '#333')"
                            @click="handleButtonClick(btn)"
                        >
                            {{ btn.text }}
                        </el-button>
                    </template>
                    <template v-else>
                        <el-button
                            v-if="showCancel"
                            class="kpl-dialog__btn kpl-dialog__btn--cancel"
                            color="#333"
                            @click="handleCancel"
                        >
                            {{ cancelText }}
                        </el-button>
                        <el-button
                            class="kpl-dialog__btn kpl-dialog__btn--confirm"
                            color="#386AEA"
                            @click="handleConfirm"
                        >
                            {{ confirmText }}
                        </el-button>
                    </template>
                </div>
            </slot>
        </template>
    </el-dialog>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: '提示',
    },
    content: {
        type: String,
        default: '',
    },
    width: {
        type: [String, Number],
        default: '422px',
    },
    showClose: {
        type: Boolean,
        default: false,
    },
    alignCenter: {
        type: Boolean,
        default: true,
    },
    closeOnClickModal: {
        type: Boolean,
        default: false,
    },
    closeOnPressEscape: {
        type: Boolean,
        default: true,
    },
    appendToBody: {
        type: Boolean,
        default: true,
    },
    showFooter: {
        type: Boolean,
        default: true,
    },
    showCancel: {
        type: Boolean,
        default: false,
    },
    confirmText: {
        type: String,
        default: '确定',
    },
    cancelText: {
        type: String,
        default: '取消',
    },
    closeOnConfirm: {
        type: Boolean,
        default: true,
    },
    closeOnCancel: {
        type: Boolean,
        default: true,
    },
    // 自定义按钮：[{ text, type: 'primary'|'default', color, className, action }]
    buttons: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'close', 'button-click'])

const visible = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
})

const closeDialog = () => {
    visible.value = false
    emit('close')
}

const handleConfirm = () => {
    emit('confirm')
    if (props.closeOnConfirm) {
        closeDialog()
    }
}

const handleCancel = () => {
    emit('cancel')
    if (props.closeOnCancel) {
        closeDialog()
    }
}

const handleButtonClick = (btn) => {
    emit('button-click', btn)
    if (btn.action === 'confirm') {
        handleConfirm()
        return
    }
    if (btn.action === 'cancel') {
        handleCancel()
        return
    }
    if (btn.close !== false) {
        closeDialog()
    }
}

const handleBeforeClose = (done) => {
    emit('close')
    done()
}
</script>

<style lang="scss">
.kpl-dialog.el-dialog {
    padding: 0;
    background: #212327;
    border-radius: 8px;
    border: none;
    box-shadow: none;

    .el-dialog__header {
        margin: 0;
        padding: 10px 18px 0;
    }

    .el-dialog__body {
        padding: 0 18px;
    }

    .el-dialog__footer {
        padding: 0 18px 18px;
    }

    .el-dialog__headerbtn {
        top: 20px;
        right: 20px;

        .el-dialog__close {
            color: rgba(255, 255, 255, 0.6);
        }
    }
}

.kpl-dialog__title {
    font-size: 16px;
    font-weight: 200;
    line-height: 22px;
    color: #E6E6E6;
    text-align: left;
}

.kpl-dialog__body {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 0 20px;
}

.kpl-dialog__content {
    width: 100%;
    font-size: 16px;
    font-weight: 200;
    line-height: 23px;
    color: #E6E6E6;
    text-align: center;
    word-break: break-word;
}

.kpl-dialog__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
}

.kpl-dialog__btn {
    min-width: 80px;
    height: 39px;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 200;
    color: #fff;

    &.kpl-dialog__btn--cancel {
        background: #333;
    }

    &.kpl-dialog__btn--confirm {
        background: #1454F4;
    }
}

.kpl-dialog__btn:hover,
.kpl-dialog__btn:focus {
    opacity: 0.9;
}
</style>
